import { DifficultyLevel } from '../interfaces/question.interface';
import { MatchMode, MatchRound } from '../interfaces/match-up.interface';

/**
 * Round generators for Star Match — numbers only, one skill per mode.
 * Every mode is "drag the numeral onto the thing that equals it", but
 * the *thing* changes so the same drag gesture teaches five different
 * early-numeracy ideas:
 *
 *   dice     : numeral → dice face            (subitising 1–6)
 *   count    : numeral → scattered pips       (counting to 9)
 *   tenframe : numeral → filled 10-frame      (making ten / place value)
 *   add      : numeral → "a + b"              (addition to 10)
 *   sub      : numeral → "a − b"              (subtraction within 10)
 *
 * Boards are built fresh (randomised) each session, with all target
 * values in a round kept distinct so every numeral matches exactly one
 * slot.
 */

const PAIRS_PER_ROUND = 4;
const MINUS = '−'; // proper minus sign, not a hyphen

function sample<T>(pool: T[], n: number): T[] {
  const copy = [...pool];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

const range = (lo: number, hi: number) => Array.from({ length: hi - lo + 1 }, (_, i) => lo + i);
const randInt = (lo: number, hi: number) => lo + Math.floor(Math.random() * (hi - lo + 1));

function makeCountRound(idx: number): MatchRound {
  const nums = sample(range(2, 9), PAIRS_PER_ROUND);
  return {
    id: `count-${idx}`, mode: 'count',
    prompt: { en: 'Match each number to the right amount', ar: 'طابِق كل رقم مع العدد الصحيح' },
    difficulty: DifficultyLevel.EASY,
    pairs: nums.map(n => ({ id: `c${n}`, tileText: String(n), slotKind: 'dots' as const, count: n }))
  };
}

function makeDiceRound(idx: number): MatchRound {
  const nums = sample(range(1, 6), PAIRS_PER_ROUND);
  return {
    id: `dice-${idx}`, mode: 'dice',
    prompt: { en: 'Match each number to its dice', ar: 'طابِق كل رقم مع النرد' },
    difficulty: DifficultyLevel.EASY,
    pairs: nums.map(n => ({ id: `d${n}`, tileText: String(n), slotKind: 'dice' as const, count: n }))
  };
}

function makeTenFrameRound(idx: number): MatchRound {
  const nums = sample(range(3, 10), PAIRS_PER_ROUND);
  return {
    id: `tf-${idx}`, mode: 'tenframe',
    prompt: { en: 'Match each number to its ten-frame', ar: 'طابِق كل رقم مع الإطار العشري' },
    difficulty: DifficultyLevel.EASY,
    pairs: nums.map(n => ({ id: `t${n}`, tileText: String(n), slotKind: 'tenframe' as const, count: n }))
  };
}

function makeAddRound(idx: number): MatchRound {
  const sums = sample(range(3, 10), PAIRS_PER_ROUND);
  return {
    id: `add-${idx}`, mode: 'add',
    prompt: { en: 'Match each number to the sum that makes it', ar: 'طابِق كل رقم مع الجمع الذي يساويه' },
    difficulty: DifficultyLevel.MEDIUM,
    pairs: sums.map(s => {
      const a = randInt(1, s - 1);
      return { id: `s${s}`, tileText: String(s), slotKind: 'expr' as const, exprText: `${a} + ${s - a}` };
    })
  };
}

function makeSubRound(idx: number): MatchRound {
  const results = sample(range(1, 8), PAIRS_PER_ROUND);
  return {
    id: `sub-${idx}`, mode: 'sub',
    prompt: { en: 'Match each number to the take-away that makes it', ar: 'طابِق كل رقم مع الطرح الذي يساويه' },
    difficulty: DifficultyLevel.MEDIUM,
    pairs: results.map(r => {
      const b = randInt(1, 10 - r);   // keep the minuend ≤ 10
      return { id: `r${r}`, tileText: String(r), slotKind: 'expr' as const, exprText: `${r + b} ${MINUS} ${b}` };
    })
  };
}

const BUILDERS: Record<MatchMode, (idx: number) => MatchRound> = {
  dice: makeDiceRound,
  count: makeCountRound,
  tenframe: makeTenFrameRound,
  add: makeAddRound,
  sub: makeSubRound
};

/**
 * Build a session's rounds, cycling the modes warm-up → harder
 * (dice → count → ten-frame → add → sub → …) so difficulty stays
 * varied and predictable across the turn rotation.
 */
export function buildMatchUpRounds(count: number): MatchRound[] {
  const order: MatchMode[] = ['dice', 'count', 'tenframe', 'add', 'sub'];
  const rounds: MatchRound[] = [];
  for (let i = 0; i < count; i++) {
    rounds.push(BUILDERS[order[i % order.length]](i));
  }
  return rounds;
}
